import { useMutation, useQueryClient } from '@tanstack/react-query';

import { QueryKey } from ':cloud/queries/types';
import { APIClientBase } from ':cloud/state/Api';
import { CreateSmartContractResponse } from ':cloud/types/service_proto';
import { CoinbaseSmartContract } from ':cloud/types/ts_types';

type CreateSmartContractsParams = {
  orgId: string;
  projectId: string;
  smartContract: CoinbaseSmartContract;
};

async function createSmartContracts({
  orgId,
  projectId,
  smartContract,
}: CreateSmartContractsParams): Promise<CreateSmartContractResponse | undefined> {
  const path = `/platform/organizations/${orgId}/projects/${projectId}/v1/networks/${smartContract.networkId}/smart_contracts/${smartContract.contractAddress}/register`;
  const body = {
    abi: smartContract.abi,
    // api doesn't use camelCase, so disabling for this line
    // eslint-disable-next-line camelcase
    contract_name: smartContract.contractName,
  };

  const response = await APIClientBase.post<CreateSmartContractResponse>(path, body);
  return response.data;
}

type CreateSmartContractMutate = {
  orgId: string;
  projectId: string;
  smartContract: CoinbaseSmartContract;
};

export function useCreateSmartContracts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ projectId, orgId, smartContract }: CreateSmartContractMutate) =>
      createSmartContracts({
        orgId,
        projectId,
        smartContract,
      }),
    onSuccess: async (newlyCreatedSmartContract) => {
      if (!newlyCreatedSmartContract) return;

      queryClient.setQueryData(
        [QueryKey.smartContracts, newlyCreatedSmartContract.smartContractId],
        newlyCreatedSmartContract,
      );
      await queryClient.invalidateQueries([QueryKey.smartContracts]);
    },
  });
}
